export const soulInLove = {
  slug: "soul-in-love",
  kicker: "An Expression of The SEAL Collective",
  title: "SOUL IN LOVE",
  tagline: "Spiritual work rooted in love, sound, and inner knowing.",
  lead: "Soul in Love is the contemplative heart of The SEAL Collective — a body of work devoted to the quiet, powerful places where sound, spirit, and self meet.",
  chapters: [
    {
      heading: "The Work",
      body: "Channeled guidance, vocal frequencies, sound experiences, teachings, and meditations — each created as an invitation to listen more deeply. This is not doctrine. It is practice: returning, again and again, to what is genuine.",
    },
    {
      heading: "The Spaces",
      body: "Soul in Love creates rooms — literal and sonic — where people can set down performance and simply be. Sessions, gatherings, and recordings designed for stillness, honest reflection, and the kind of hearing that changes things.",
    },
    {
      heading: "The Ground",
      body: "Everything here is rooted in one premise: love is not a mood, it is a frequency — steady, available, and already present beneath the noise. The work is simply helping you hear it.",
    },
  ],
  keyline: "DEEPER LISTENING CHANGES EVERYTHING.",
  externalKicker: "Continue the Journey",
  externalHeading: "THE WORK LIVES AT SOULINLOVE.COM",
  externalIntro:
    "Sessions, courses, retreats, the sound library, music, and community — the living home of Soul in Love.",
  externalLinks: [
    { label: "Music", sub: "Songs Amber sings", url: "https://soulinlove.com/music" },
    { label: "Sound Library", sub: "Meditations & frequencies", url: "https://soulinlove.com/sound-library" },
    { label: "Sessions", sub: "One-to-one work", url: "https://soulinlove.com/sessions" },
    { label: "Courses", sub: "Teachings & practices", url: "https://soulinlove.com/courses" },
    { label: "Retreats", sub: "Immersive gatherings", url: "https://soulinlove.com/retreats" },
    { label: "Community", sub: "The circle", url: "https://soulinlove.com/community" },
  ],
  ctaKicker: "Soul in Love",
  ctaHeading: "STEP INTO DEEPER LISTENING.",
  interest: "Soul in Love",
};

export const amberEllis = {
  slug: "amber-ellis",
  kicker: "An Expression of The SEAL Collective",
  title: "AMBER ELLIS",
  tagline: "Voice as art. Voice as expression. Voice as instrument.",
  lead: "Recording artist, professional vocalist, voice talent, and creator — Amber Ellis brings decades of performance, communication, teaching, and vocal experience into every note and every word.",
  chapters: [
    {
      heading: "The Artist",
      body: "Music written and recorded from the same center the Collective is built on. Songs as vessels — for emotion, for memory, for the things that only melody can carry.",
    },
    {
      heading: "The Voice",
      body: "Professional voiceover and narration shaped by decades behind the microphone. Commercial, cinematic, contemplative — a voice trained to hold attention and to hold meaning.",
    },
    {
      heading: "The Immersion",
      body: "Beyond recording: immersive vocal work, live sound experiences, and vocal teaching — helping others find the instrument they have been carrying all along.",
    },
  ],
  keyline: "THE VOICE IS THE OLDEST INSTRUMENT.",
  externalKicker: "Listen",
  externalHeading: "THE MUSIC LIVES AT SOULINLOVE.COM",
  externalIntro:
    "For now, Amber's music is housed under Soul in Love — a dedicated music home is being composed.",
  externalLinks: [
    { label: "Music", sub: "Listen to Amber's songs", url: "https://soulinlove.com/music" },
    { label: "Sound Library", sub: "Immersive vocal work", url: "https://soulinlove.com/sound-library" },
  ],
  ctaKicker: "Amber Ellis",
  ctaHeading: "CREATE SOMETHING WITH AMBER.",
  interest: "Amber Ellis",
};

export const tundala = {
  slug: "tundala",
  kicker: "An Emerging Expression of The SEAL Collective",
  title: "TUNDALA",
  tagline: "A companion for sound, voice, and the states between.",
  lead: "Tundala is an emerging sound-based companion exploring tone, chant, voice, breath, resonance, and frequency-informed audio — a way to work with sound not as background, but as a practice.",
  chapters: [
    {
      heading: "The Practice",
      body: "Chants, tones, guided sound, and vocal experiences — Tundala grows from the living sound work already shared through Soul in Love: meditations, vocal frequencies, and sound experiences people return to daily.",
    },
    {
      heading: "The Companion",
      body: "Tundala is being shaped as something you carry with you — a companion for the morning, the transition, the difficult hour, the return to center. Not an app that demands your attention, but a presence that helps you shift your state and meet the next moment well.",
    },
    {
      heading: "The Horizon",
      body: "A dedicated Tundala experience — envisioned as a downloadable companion — is being composed. Until it arrives, the sound work lives and grows within the Soul in Love sound library.",
    },
  ],
  keyline: "YOUR VOICE IS ALREADY CARRYING SOMETHING.",
  externalKicker: "Experience the Sound Today",
  externalHeading: "THE SOUND WORK LIVES AT SOULINLOVE.COM",
  externalIntro:
    "Chants, meditations, vocal frequencies, and music — the growing library Tundala is born from.",
  externalLinks: [
    { label: "Sound Library", sub: "Chants, meditations & frequencies", url: "https://soulinlove.com/sound-library" },
    { label: "Music", sub: "Songs and vocal work", url: "https://soulinlove.com/music" },
  ],
  ctaKicker: "Tundala",
  ctaHeading: "FOLLOW TUNDALA AS IT EMERGES.",
  interest: "Tundala",
};

export const organizations = {
  slug: "organizations",
  seoTitle: "SEAL for Organizations | Employee Well-Being, Conscious Leadership & Workplace Resonance",
  seoDescription:
    "SEAL for Organizations helps companies invest in the mind, body, and spirit of their people — simple, heart-led practices that reset focus, build connection, and support human-centered growth.",
  kicker: "An Expression of The SEAL Collective",
  title: "SEAL FOR ORGANIZATIONS",
  tagline: "Human-centered growth begins with human beings.",
  lead: "The most valuable asset in your organization is not your strategy, your product, or your technology. It is the state of your people. SEAL for Organizations helps companies care for the mind, body, and spirit of their teams — because when the people rise, everything they touch rises with them.",
  chapters: [
    {
      heading: "The Premise",
      body: "Every organization runs on a frequency. You can feel it in the first five minutes of a meeting — the clarity or the static, the trust or the tension. Strategy decks don't change that signal. People do. SEAL for Organizations works at the level where culture actually lives: in the nervous systems, voices, and hearts of the humans who show up every day.",
    },
    {
      heading: "The Ground",
      body: "This work is simple at its core. People do their best thinking, creating, and collaborating when they feel well — calm in their bodies, clear in their minds, and connected to each other. Every tradition of human wisdom, and the everyday experience of every great team, points to the same truth: when people are centered, everything works better. SEAL for Organizations simply builds from that center.",
    },
    {
      heading: "The Practice — Reset and Ignite",
      body: "SEAL gives organizations simple, elegant tools that fit inside a real workday. Voice-led resets that return a scattered room to focus in minutes. Mindfulness and mindset practices that don't require incense or an offsite. Communication and presence training that helps people speak from center instead of from stress. Some days a team needs to exhale. Some days it needs fire. SEAL teaches leaders to read the room's frequency — and move it.",
    },
    {
      heading: "The Return",
      body: "Investing in the mind, body, and spirit of your people is not a benefit line item — it is the highest-leverage growth strategy available to you. Well-supported humans stay longer, create more, sell with conviction, and serve with genuine care. When the vibrational frequency of your people rises, the entire structure — culture, brand, revenue, reputation — reorganizes around it. This is heart math: the most rational investment a company can make.",
    },
  ],
  scienceTitle: "WHY IT MATTERS",
  science: [
    "People do their best work when they feel well in mind, body, and spirit.",
    "A calm, centered team communicates better than a stressed one.",
    "Voice and breath are simple, natural ways to reset focus — available to everyone, every day.",
    "Companies that genuinely care for their people tend to keep them longer and grow stronger together.",
  ],
  programsTitle: "SALES FROM THE HEART — CORPORATE SESSIONS",
  programsIntro:
    "Sales enablement, reimagined. These are live corporate sessions — led by Amber and available with a full facilitation team — that teach sales, communication, and connection from the heart. Human first. Numbers follow.",
  programs: [
    {
      name: "The Keynote",
      format: "60–90 minutes · Full company",
      text: "An opening spark: Sales from the Heart as a philosophy. Amber sets the frequency for your whole organization — presence, connection, and what selling feels like when it comes from center.",
    },
    {
      name: "The Enablement Session",
      format: "Half day · Sales teams",
      text: "A working session for sales teams: heart-led discovery, listening that hears what's unspoken, voice and presence under pressure, and conversations that move people instead of pushing them.",
    },
    {
      name: "The Team Series",
      format: "Multi-session · Full team with facilitation",
      text: "A deeper engagement delivered with a full facilitation team — ongoing sessions that weave sales enablement, communication, mindfulness, and culture into the daily rhythm of your organization.",
    },
  ],
  pillarsTitle: "WAYS WE WORK TOGETHER",
  pillars: [
    {
      name: "Leadership & Presence",
      text: "Coaching and training that develops leaders people genuinely want to follow — grounded, clear, and human under pressure.",
    },
    {
      name: "Communication & Voice",
      text: "Voice and presence work that transforms how teams speak, listen, and land a message — in the boardroom and beyond.",
    },
    {
      name: "Mindfulness & Mindset",
      text: "Simple, practical practices that reset focus, ease everyday stress, and build resilient, optimistic cultures.",
    },
    {
      name: "Culture & Employee Experience",
      text: "Rituals, rhythms, and environments designed so well-being is built into the workday — not bolted onto it.",
    },
    {
      name: "Team Development",
      text: "Experiences that move groups from coordination to genuine coherence — the state where great work becomes easy.",
    },
    {
      name: "Sales & Training Excellence",
      text: "Decades of corporate sales and training experience, translated into programs that actually change behavior.",
    },
  ],
  keyline: "WHAT MOVES THROUGH THE HEART CAN MOVE AN ORGANIZATION.",
  ctaKicker: "SEAL for Organizations",
  ctaHeading: "RAISE THE FREQUENCY OF YOUR ENTIRE ORGANIZATION.",
  interest: "Organizations",
  special: "orgapp",
};
